<template>
  <div>
    <Loader v-if="isLoading" />
    <Page v-else>
      <Breadcrumbs root-path="/loans" />

      <div class="container">
        <Header :reference-id="referenceId" :loan-details="details" />

        <div class="tabs-container scrollbar-container">
          <button
            v-for="tab in tabs"
            :key="tab.identifier"
            :class="{
              'tab-button': true,
              active: activeTab === tab.identifier,
            }"
            @click="selectTab(tab.identifier)"
          >
            {{ tab.name }}
          </button>
        </div>
        <component
          ref="dynamicComponentRef"
          :is="activeTabComponent"
          v-bind="activeTabProps"
          v-on="activeTabListeners"
          class="tab-content"
        />
      </div>
    </Page>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import axios from "axios";
import Page from "@/components/layout/Page.vue";
import Loader from "@/components/Loader.vue";
import Breadcrumbs from "@/components/shared/Breadcrumbs.vue";
import Header from "@/components/loan/Header.vue";
import DetailsTab from "@/components/loan/tabs/DetailsTab.vue";
import DrawsTab from "@/components/loan/tabs/DrawsTab.vue";
import RefiDisbursementsTab from "@/components/loan/tabs/RefiDisbursementsTab.vue";
import AnnualReviewTab from "@/components/loan/tabs/AnnualReviewTab.vue";
import DocumentsTab from "@/components/loan/tabs/DocumentsTab.vue";
import CommentsTab from "@/components/loan/tabs/CommentsTab.vue";
import { LoanDetails } from "@/models/loans";
import functions from "@/use/functions.js";
import { FormModes } from "@/types/form";

interface TabViewModel {
  name: string;
  identifier: string;
}

const route = useRoute();
const router = useRouter();

const dynamicComponentRef = ref<any>(null);

const isLoading = ref<boolean>(true);
const details = ref({} as LoanDetails);
const referenceId = ref<string>();
const documentEntityId = ref<number>();
const loanId = ref<number>();
const tabs = ref<TabViewModel[]>([]);
const activeTab = ref<string>("details");

const tabComponents = {
  details: DetailsTab,
  draws: DrawsTab,
  "refi-disbursements": RefiDisbursementsTab,
  "annual-review": AnnualReviewTab,
  documents: DocumentsTab,
  comments: CommentsTab,
};

const activeTabComponent = computed(
  () => tabComponents[activeTab.value] || null
);

const activeTabListeners = computed(() => {
  switch (activeTab.value) {
    case "draws":
      return {
        studentUpdated: async () => {
          await refreshStudentData();
        },
      };
    default:
      return {};
  }
});
const activeTabProps = computed(() => {
  switch (activeTab.value) {
    case "details":
      return { loanDetails: details.value, onUpdateLoan: getLoanAsync };
    case "draws":
      return {
        loanId: loanId.value,
        loanDetails: details.value,
        availableToDrawAmount: details.value?.loanAmount,
        programTypeId: details.value?.loan?.programTypeId,
        productTypeId: details.value?.loan?.productType,
        lenderId: details.value?.loan?.lenderId,
        minimumDrawAmount: details.value?.minimumDrawAmount,
        onDrawsChanged: getLoanAsync,
      };
    case "refi-disbursements":
      return {
        loanId: loanId.value,
        lenderId: details.value?.loan?.lenderId,
        onPayoffsChanged: getLoanAsync,
      };
    case "documents":
      return {
        documentEntityId: details.value?.documentEntityId,
        loanId: details.value?.loanId,
        borrowers: details.value?.borrowers,
        referenceId: details.value?.loan.referenceId
      };
    case "annual-review":
      return {
        loanId: loanId.value,
        lenderId: details.value.loan.lenderId,
        programTypeId: details.value.loan.programTypeId,
        productTypeId: details.value.loan.productType,
        referenceId: details.value.loan.referenceId,
        applicationUrl: details.value.applicationUrl,
        annualReviews: details.value.annualReviews,
      };
    case "comments":
      return {
        loanId: loanId.value,
      };
    default:
      return {};
  }
});

const tabParameterMap: Record<string, string[]> = {
  draws: ["drawId", "disbursementId", "studentDetailsExpanded"],
  "refi-disbursements": ["payoffSortKey", "payoffSortOrder"],
  documents: ["documentsSortKey", "documentsSortOrder"],
  "annual-review": [
    "annualReviewId",
    "alertsSortKey",
    "alertsSortOrder",
    "knockoutsSortKey",
    "knockoutsSortOrder",
  ],
};

function selectTab(tabIdentifier: string) {
  activeTab.value = tabIdentifier;

  const allowedParams = tabParameterMap[tabIdentifier] || [];

  const newQuery: Record<string, string | undefined> = {};

  // persist params for same tab, otherwise clear
  for (const key of Object.keys(route.query)) {
    if (allowedParams.includes(key)) {
      newQuery[key] = route.query[key] as string;
    }
  }

  router.replace({
    query: newQuery,
    hash: `#${tabIdentifier}`,
  });
}

async function refreshStudentData() {
  try {
    const response = await axios(
      `/api/loanservice/loans/details/ref/${referenceId.value}`
    );

    details.value = { ...details.value, student: response.data.student };
    loanId.value = response.data.loan.id;
    documentEntityId.value = response.data.entityId;
    tabs.value = response.data.tabs;

    if (
      dynamicComponentRef.value &&
      dynamicComponentRef.value?.studentFormRef
    ) {
      dynamicComponentRef.value?.studentFormRef?.changeMode(FormModes.view);
    }

    functions.closeModal();
  } catch (error) {
    router.push("/page-not-found");
  }
}

async function getLoanAsync() {
  try {
    const response = await axios(
      `/api/loanservice/loans/details/ref/${referenceId.value}`
    );

    details.value = response.data;
    loanId.value = response.data.loan.id;
    documentEntityId.value = response.data.entityId;
    tabs.value = response.data.tabs;
  } catch (error) {
    router.push("/page-not-found");
  }
}

onMounted(async () => {
  const id = route.params.referenceId;

  referenceId.value = String(id);

  await getLoanAsync();

  const initialTab = window.location.hash.replace("#", "");

  if (initialTab && tabs.value.some((tab) => tab.identifier === initialTab)) {
    selectTab(initialTab);
  }

  functions.logPageView({
    id: referenceId.value,
    page: "loans",
    category: "Loan",
    timestamp: Date.now(),
    firstName: details.value.borrowers[0]?.firstName,
    lastName: details.value.borrowers[0]?.lastName,
    name: details.value.borrowers[0]?.fullName,
    lenderId: details.value.loan?.lenderId,
    lenderName: details.value.lenderName,
    programName: details.value.programName,
  });

  isLoading.value = false;
});
</script>

<style scoped>
.container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  padding-left: 1rem;
  font-family: "Montserrat", arial, sans-serif;
}

.tabs-container {
  display: flex;
  margin-top: 22px;
  gap: 0.5rem;
  padding-left: 1rem;
  overflow-x: auto;
  white-space: nowrap;
}

@media (max-width: 960px) {
  .container {
    padding-left: 0;
  }

  .tabs-container {
    padding-left: 0.5rem;
  }
}

.tab-button {
  flex-shrink: 0;
  padding: 0.75rem 1.5rem;
  background-color: #ccc;
  color: #555;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  cursor: pointer;
  white-space: nowrap;
  font-weight: 600;
  font-size: 15px;
  border-top: 1px solid transparent;
  border-left: 1px solid transparent;
  border-right: 1px solid transparent;
  border-bottom: none;
  font-family: "Montserrat", arial, sans-serif;
  transition: background-color 0.3s ease, color 0.3s ease;
}

.tab-button:hover {
  background-color: #bbb;
}

.tab-button.active {
  background-color: white;
  color: black;
  border-top: 1px solid #ccc;
  border-left: 1px solid #ccc;
  border-right: 1px solid #ccc;
}

.tab-content {
  flex-grow: 1;
  padding: 1rem 1rem 1.5rem 1.5rem;
  background-color: white;
}
</style>

<style src="@/assets/css/scrollbar.css"></style>
